<template>
    <form
        @submit.prevent="handleSubmit"
        class="form-container advance-search-form claimsvault"
        :class="{ result_mode: form_mode == 'results' }"
        data-cy="advance-search-container"
    >
        <div class="form-section">
            <div class="section-body header">
                <div class="w-full">
                    <span class="text-2xl text-default-purple">{{ $t('claims.search_form.title.advanced') }}</span>
                    <span class="text-xl">{{ $t('claims.search_form.title.search') }}</span>
                </div>
                <div class="w-full" v-if="form_mode == 'init'">
                    <span>{{ $t('claims.search_form.description') }}</span>
                </div>
            </div>
            <div class="section-body fields">
                <div class="flex flex-wrap w-full">
                    <div
                        class="input-container px-2 mb-2"
                        :class="{ 'md:w-1/3': form_mode == 'init', 'md:w-1/4': form_mode == 'results' }"
                        data-cy="claims-search-name-field"
                    >
                        <ui-label>{{ $t('claims.search_form.input_fields.name') }}</ui-label>
                        <ui-input v-model="search_form.name" />
                    </div>
                    <div
                        class="input-container px-2 mb-2"
                        :class="{ 'md:w-1/3': form_mode == 'init', 'md:w-1/4': form_mode == 'results' }"
                        data-cy="claims-search-national-id-field"
                    >
                        <ui-label>{{ $t('claims.search_form.input_fields.national_id') }}</ui-label>
                        <ui-input v-model="search_form.national_id" />
                    </div>
                    <div
                        class="input-container px-2 mb-2"
                        :class="{ 'md:w-1/3': form_mode == 'init', 'md:w-1/4': form_mode == 'results' }"
                        data-cy="claims-search-registration-no-field"
                    >
                        <ui-label>{{ $t('claims.search_form.input_fields.reg_no') }}</ui-label>
                        <ui-input v-model="search_form.reg_no" />
                    </div>
                    <div
                        class="input-container px-2 mb-2"
                        :class="{ 'md:w-1/3': form_mode == 'init', 'md:w-1/4': form_mode == 'results' }"
                        data-cy="claims-search-chassis-no-field"
                    >
                        <ui-label>{{ $t('claims.search_form.input_fields.chassis_no') }}</ui-label>
                        <ui-input v-model="search_form.chassis_no" />
                    </div>
                    <div
                        class="input-container px-2 mb-2"
                        :class="{ 'md:w-1/3': form_mode == 'init', 'md:w-1/4': form_mode == 'results' }"
                        data-cy="claims-search-engine-no-field"
                    >
                        <ui-label>{{ $t('claims.search_form.input_fields.engine_no') }}</ui-label>
                        <ui-input v-model="search_form.engine_no" />
                    </div>
                    <div
                        class="input-container px-2 mb-2"
                        :class="{ 'md:w-1/3': form_mode == 'init', 'md:w-1/4': form_mode == 'results' }"
                        data-cy="claims-search-years-field"
                    >
                        <ui-label>{{ $t('claims.search_form.input_fields.years_from') }}</ui-label>
                        <a-select v-model:value="search_form.years_from">
                            <a-select-option v-for="year in years" :key="year.key">
                                {{ year.value }}
                            </a-select-option>
                        </a-select>
                    </div>
                    <div
                        class="input-container px-2 mb-2"
                        :class="{ 'md:w-2/3': form_mode == 'init', 'md:w-1/2': form_mode == 'results' }"
                        data-cy="claims-search-risk-location-field"
                    >
                        <ui-label>{{ $t('claims.search_form.input_fields.risk_location') }}</ui-label>
                        <ui-input v-model="search_form.risk_location" />
                    </div>
                </div>
            </div>
            <div class="section-body radio-buttons">
                <div class="flex w-full">
                    <div class="flex flex-wrap w-full">
                        <a-radio-group
                            class="input-container checkbox-container w-auto text-left"
                            v-model:value="search_form.all_match"
                        >
                            <a-radio data-cy="claims-search-all-match-checkbox" :value="1">
                                {{ $t('claims.search_form.checkboxes.all_match') }}
                            </a-radio>
                            <a-radio data-cy="claims-search-any-match-checkbox" :value="0">
                                {{ $t('claims.search_form.checkboxes.any_match') }}
                            </a-radio>
                        </a-radio-group>
                    </div>
                </div>
            </div>
            <div class="section-body search-buttons" v-if="form_mode == 'init'">
                <div class="flex w-full justify-between">
                    <div>
                        <a-button data-cy="button-cancel" @click="handleCancel" class="mx-2" :disabled="processing">
                            {{ $t('claims.search_form.buttons.cancel') }}
                        </a-button>
                    </div>
                    <div>
                        <a
                            data-cy="button-clear"
                            @click="handleClear"
                            class="mx-2 underline text-default-purple hover:text-highlight-purple"
                            >{{ $t('claims.search_form.buttons.clear') }}</a
                        >
                        <ui-submit-button class="mx-2" data-cy="button-find" :disabled="processing">
                            <span v-if="!processing">{{ $t('claims.search_form.buttons.find') }}</span>
                            <span v-else>{{ $t('claims.search_form.buttons.finding') }}</span>
                        </ui-submit-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-section result_mode_buttons w-1/6 lg:w-2/12 xl:w-1/12" v-if="form_mode == 'results'">
            <div class="pt-5 border-l h-full px-2">
                <div class="mt-16">
                    <ui-submit-button class="mx-2" data-cy="button-find" :disabled="processing">
                        <span v-if="!processing">{{ $t('claims.search_form.buttons.find') }}</span>
                        <span v-else>{{ $t('claims.search_form.buttons.finding') }}</span>
                    </ui-submit-button>
                </div>
                <div class="mt-4">
                    <a
                        data-cy="button-clear"
                        @click="handleClear"
                        class="mx-2 underline text-default-purple hover:text-highlight-purple"
                        >{{ $t('claims.search_form.buttons.clear') }}</a
                    >
                </div>
                <div class="mt-8">
                    <a-button data-cy="button-cancel" @click="handleCancel" class="mx-2" :disabled="processing">
                        {{ $t('claims.search_form.buttons.cancel') }}
                    </a-button>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';

export default {
    components: {
        UiCheckbox,
        UiInput,
        UiLabel,
        UiSubmitButton,
    },
    computed: {
        years() {
            var max = new Date().getFullYear();
            var min = max - 100;
            var years = [
                {
                    key: 'all',
                    value: this.$t('claims.search_form.value.year_all'),
                },
            ];

            for (var year = max; year >= min; year--) {
                years.push({
                    key: year,
                    value: year,
                });
            }
            return years;
        },
    },
    props: {
        processing: {
            type: Boolean,
            default: false,
        },
        search_form: {
            type: Object,
            default: {},
        },
        form_mode: {
            type: String,
            default: 'init',
        },
    },
    methods: {
        handleCancel() {
            this.$emit('cancel');
        },
        handleClear() {
            this.$emit('clear');
        },
        handleSubmit() {
            this.$emit('formSubmit');
        },
    },
};
</script>
